import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Shared PayPal Smart Button.
 *
 * Props:
 *   kind           "package" | "subscription"
 *   email          string (required — buyer email)
 *   firstName      string (required)
 *   lastName       string (optional — package only)
 *   packageId      string (required for kind="package")
 *   plan           "monthly" | "annual" (required for kind="subscription")
 *   disabled       boolean — disable until form is valid
 *   onSuccess(id)  callback invoked after capture/activate succeeds
 */
export const PayPalButton = ({
  kind,
  email,
  firstName,
  lastName = "",
  packageId,
  plan = "monthly",
  discountCode = null,
  disabled = false,
  onSuccess,
}) => {
  const [config, setConfig] = useState(null);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/paypal/config`)
      .then(({ data }) => {
        if (mounted && data?.enabled && data?.client_id) setConfig(data);
      })
      .catch(() => {
        /* silently hide PayPal if config fails */
      });
    return () => {
      mounted = false;
    };
  }, []);

  if (!config) return null;

  const sdkOptions = {
    "client-id": config.client_id,
    currency: config.currency || "AUD",
    intent: kind === "subscription" ? "subscription" : "capture",
    vault: kind === "subscription" ? "true" : "false",
    components: "buttons",
    "enable-funding": kind === "subscription" ? "" : "paylater",
  };

  return (
    <div className="mt-3" data-testid="paypal-button-container">
      {disabled && (
        <p className="text-xs text-center text-[var(--ocean)] mb-2 italic">
          Fill in your details above to enable PayPal
        </p>
      )}
      <div className={disabled ? "opacity-50 pointer-events-none" : ""}>
        <PayPalScriptProvider options={sdkOptions}>
          {kind === "package" ? (
            <PayPalButtons
              style={{ layout: "horizontal", color: "blue", shape: "pill", label: "paypal", tagline: false, height: 44 }}
              disabled={disabled}
              forceReRender={[email, firstName, lastName, packageId, discountCode]}
              createOrder={async () => {
                try {
                  const { data } = await axios.post(`${API}/paypal/order/create`, {
                    package_id: packageId,
                    email: email.trim(),
                    first_name: firstName.trim(),
                    last_name: lastName.trim(),
                    origin_url: window.location.origin,
                    discount_code: discountCode || null,
                  });
                  return data.order_id;
                } catch (e) {
                  toast.error("Couldn't start PayPal — try Stripe or refresh.");
                  throw e;
                }
              }}
              onApprove={async (data) => {
                try {
                  await axios.post(`${API}/paypal/order/${data.orderID}/capture`);
                  toast.success("Payment received — agreement on its way!");
                  onSuccess?.(data.orderID);
                } catch (e) {
                  toast.error("Payment captured but our system hiccupped — check your email.");
                }
              }}
              onError={(err) => {
                console.error("PayPal error:", err);
                toast.error("PayPal hit a snag — please use Stripe or try again.");
              }}
              onCancel={() => {
                toast.info("PayPal cancelled — you can use Stripe instead.");
              }}
            />
          ) : (
            <PayPalButtons
              style={{ layout: "horizontal", color: "blue", shape: "pill", label: "subscribe", tagline: false, height: 44 }}
              disabled={disabled}
              forceReRender={[email, firstName, plan]}
              createSubscription={async () => {
                try {
                  const { data } = await axios.post(`${API}/paypal/subscription/create`, {
                    email: email.trim(),
                    first_name: firstName.trim(),
                    plan,
                    origin_url: window.location.origin,
                  });
                  return data.subscription_id;
                } catch (e) {
                  toast.error("Couldn't start PayPal subscription — try Stripe.");
                  throw e;
                }
              }}
              onApprove={async (data) => {
                try {
                  await axios.post(`${API}/paypal/subscription/${data.subscriptionID}/activate`);
                  toast.success("Welcome to Members — check your inbox!");
                  onSuccess?.(data.subscriptionID);
                } catch (e) {
                  toast.error("Subscription created — your members link is on its way.");
                  onSuccess?.(data.subscriptionID);
                }
              }}
              onError={(err) => {
                console.error("PayPal subscription error:", err);
                toast.error("PayPal hit a snag — please try Stripe.");
              }}
              onCancel={() => {
                toast.info("PayPal cancelled — you can use Stripe instead.");
              }}
            />
          )}
        </PayPalScriptProvider>
      </div>
    </div>
  );
};
